import React, { useEffect, useState, useCallback } from "react";
import { useSearch } from "../Contexts/SearchContext";
import Loading from "../Loading";
import {
  getSuggestionHandler,
  postSearchHandler_customerSuccess,
} from "../Api";
import SearchResults from "./SearchResults/SearchResults";
import {
  setFilterState,
  getActiveFilters,
  updateDropdownHeadingLabels,
  resetFilters,
} from "../../helpers";
import FilterDrawer from "./FilterDrawer/FilterDrawer";

import ElasticCarousel from "../ElasticCarousel";
import "./SearchContainer.scss";
import Popover from "../Popover";
import SearchBar from "./SearchBar/SearchBar"
import './FilterDrawer/FilterDrawer.scss'

const SearchContainer = () => {
  const { state, dispatch } = useSearch();

  // context
  const {
    filteredResults,
    emptyResults,
    completeSearchResultSet,
    selectedFilters,
    isReady,
    filterDrawerIsOpen,
    initialRenderRequired,
    searchError,
    isLoading,
    showResults,
    placeholderText,
    componentElement,
    searchTerm,
    mostPopularProductsArray,
    componentType,
    resultsToShow_desktop
  } = state;

  const [countMessage, setCountMessage] = useState("");

  useEffect(() => {
    if (!isReady) {
      dispatch({ type: "SET_IS_LOADING", payload: true });
      dispatch({ type: "UPDATE_READINESS", payload: true });
    }
  }, [isReady]);

  /**
   * Runs a filter search API request if one is pending and
   * if filters are present. Sets the pending flag to false.
   */
  useEffect(() => {
    if (initialRenderRequired) {
      dispatch({ type: "SET_INITIAL_RENDER_REQUISITE", payload: false });
    }
  }, [initialRenderRequired]);

  useEffect(() => {
    if (filteredResults) {
      dispatch({ type: "UPDATE_SHOW_RESULTS", payload: true });
      const displayResultCount =
        resultsToShow_desktop > filteredResults.length
          ? filteredResults.length
          : resultsToShow_desktop;
      setCountMessage(
        `Showing results ${displayResultCount} out of ${filteredResults.length}`
      );
    }
  }, [filteredResults, resultsToShow_desktop]);

  /**
   * Updates the url query params each time filters are updated.
   */
  useEffect(() => {
    if (initialRenderRequired) return;

    const url = new URL(window.location.href);

    let filterParams = [];
    selectedFilters.map((filter) => {
      let filterId = encodeURI(filter.tagId);
      if (!filterParams.includes(filterId)) {
        filterParams.push(filterId);
      }
    });

    if (filterParams.length > 0) {
      url.searchParams.set("filters", filterParams.join("&"));
    } else {
      url.searchParams.delete("filters");
    }

    window.history.replaceState({}, "", url.href);
  }, [selectedFilters]);

  const getSuggestions = (_searchTerm) => {
    return getSuggestionHandler(_searchTerm, dispatch);
  };

  const doSearch = (_searchTerm, dispatch) => {
    // _searchTerm comes from when a user clicks an item in the popular search terms or suggestions dropdowns
    // it should take priority
    let term = _searchTerm || searchTerm;
    return postSearchHandler_customerSuccess(term, dispatch);
  };

  const handleClick = (filterId) => {
    setFilterState(filterId, componentElement, false);
    dispatch({
      type: "UPDATE_SELECTED_FILTERS",
      payload: getActiveFilters(componentElement),
    });
    updateDropdownHeadingLabels(componentElement);
  };
  const handleRemoveClick = () => {
    resetFilters(componentElement);
    dispatch({
      type: "UPDATE_SELECTED_FILTERS",
      payload: [],
    });
  };

  const renderSortByIcon = `<div class='cmp-product-finder-icon'><svg xmlns="http://www.w3.org/2000/svg" width="20.57" height="18" viewBox="0 0 20.57 18"><path id="Icon_awesome-sort-amount-down" data-name="Icon awesome-sort-amount-down" d="M12.217,17.678H9.645A.643.643,0,0,0,9,18.322v1.286a.643.643,0,0,0,.643.643h2.572a.643.643,0,0,0,.643-.643V18.322a.643.643,0,0,0-.643-.643ZM7.074,15.107H5.145V2.893A.643.643,0,0,0,4.5,2.25H3.217a.643.643,0,0,0-.643.643V15.107H.645a.643.643,0,0,0-.454,1.1l3.214,3.857a.643.643,0,0,0,.909,0L7.529,16.2a.644.644,0,0,0-.455-1.1ZM17.36,7.393H9.645A.643.643,0,0,0,9,8.036V9.321a.643.643,0,0,0,.643.643H17.36A.643.643,0,0,0,18,9.322V8.036A.643.643,0,0,0,17.36,7.393Zm-2.572,5.143H9.645A.643.643,0,0,0,9,13.178v1.286a.643.643,0,0,0,.643.643h5.143a.643.643,0,0,0,.643-.643V13.178a.643.643,0,0,0-.643-.643ZM19.931,2.25H9.645A.643.643,0,0,0,9,2.893V4.178a.643.643,0,0,0,.643.643H19.931a.643.643,0,0,0,.643-.643V2.893A.643.643,0,0,0,19.931,2.25Z" transform="translate(-0.004 -2.25)" fill="#3f4145"/></svg><div class="icon">&rsaquo;</div></div>`;
  const renderCarousel = (
    completeSearchResultSet,
    carouselLabel,
    isDynamic,
    fieldName
  ) => {
    const carouselItems = [];
    let relevantItems = [];
    if (fieldName === "most_popular") {
      for (let i = 0; i < mostPopularProductsArray.length; i++) {
        completeSearchResultSet.filter((item) => {
          if (item.Fields.pagePath.Value === mostPopularProductsArray[i]) {
            relevantItems.push(item);
          }
        });
      }
    } else {
      relevantItems = completeSearchResultSet.filter((item) => {
        return item.Fields[fieldName]?.Value === "true";
      });
    }

    if (relevantItems && relevantItems.length > 0 && isDynamic) {
      relevantItems.map((item) => {
        const { Fields } = item;
        const {
          pagePath,
          pageTitle_product,
          pageDescription_product,
          textCTALink_product,
          textCTAText_product,
          buttonCTAText_product,
          buttonCTALink_product,
          cardTagsAndLabels_product,
          formattedPageTags,
          byLabel_product,
        } = Fields;
        const tags = JSON.parse(cardTagsAndLabels_product?.Value);
        const cardLabel = tags[0]?.label || "";

        carouselItems.push({
          pagePath: pagePath?.Value,
          label: cardLabel,
          title: pageTitle_product?.Value,
          author: byLabel_product?.Value,
          description: pageDescription_product?.Value,
          categories: JSON.parse(formattedPageTags?.Value),
          buttonCTALink: buttonCTALink_product?.Value,
          buttonCTAText: buttonCTAText_product?.Value,
          textCTALink: textCTALink_product?.Value,
          textCTAText: textCTAText_product?.Value,
        });
      });
    }
    const loadingStatus = false;
    let dataItems = isDynamic ? carouselItems : relevantItems;
    if (dataItems.length > 6) {
      // Trim array if there are more than 6 results.
      dataItems = dataItems.slice(0, 6);
    }

    return (
      <>
        {dataItems.length ? (
          <ElasticCarousel
            carouselLabel={carouselLabel}
            carouselData={dataItems}
            loadingStatus={loadingStatus}
            componentType={componentType}
          />
        ) : null}
      </>
    );
  };

  // renders
  return (
    <section className="cmp-product-finder">
      <div className="cmp-product-finder__left-rail">
        <FilterDrawer
          postSearchHandler={doSearch}
          getSuggestionHandler={getSuggestions}
          placeholderText={placeholderText}
        />
      </div>
      <div
        className={`cmp-product-finder__main-section${filterDrawerIsOpen ? " hide" : ""
          }`}
      >
        {/* <div className="cmp-product-finder__popular-categories">
          <PopularCategories />
        </div> */}

        {/* {!showResults && completeSearchResultSet && (
          <>
            <section className="cmp-product-finder-carousel cmp-product-finder-carousel-results">
              {renderCarousel(
                completeSearchResultSet,
                "Recommended For You",
                true,
                "recommended_product",
              )}
            </section>

            <section className="cmp-product-finder-carousel cmp-product-finder-carousel-results">
              {renderCarousel(
                completeSearchResultSet,
                "Featured",
                true,
                "featured_product",
              )}
            </section>

            <section className="cmp-product-finder-carousel cmp-product-finder-carousel-results">
              {renderCarousel(
                completeSearchResultSet,
                "Most Popular",
                true,
                "most_popular",
              )}
            </section>
          </>
        )} */}
        <div className="cmp-product-finder__search-container">
          <SearchBar
            postSearchHandler={doSearch}
            getSuggestionHandler={getSuggestions}
            searchLabel="Find a product: "
            placeholderText="Search Customer Stories"
          />
        </div>

        <div className="cmp-product-finder__selected-filter-buttons">
          {selectedFilters &&
            selectedFilters.map((filterObject) => {
              if (filterObject.type === "filter") {
                return (
                  <>
                    <div
                      className="selected-filter-button"
                      data-filter-id={filterObject.tagId}
                    >
                      <div className="selected-filter-button__text">
                        {filterObject.label}
                      </div>
                      <div className="close-wrapper">
                        <div
                          className="close"
                          onClick={() => handleClick(filterObject.tagId)}
                        ></div>
                      </div>
                    </div>
                  </>
                );
              }
            })}
          {selectedFilters && <span className="remove-filters hide" onClick={() => handleRemoveClick()}>Remove all</span>}
        </div>


        {showResults && (
          <div className="cmp-product-finder__filtered-results-count">
            <div className="count-message">{countMessage}</div>
            <div className="cmp-product-finder__filtered-results-options">
              <Popover
                trigger={renderSortByIcon}
                dispatch={dispatch}
                componentElement={componentElement}
                sitetype="Product Finder"
                portal={[
                  {
                    id: 1,
                    content: "Featured Products",
                    type: "featured",
                  },
                  {
                    id: 2,
                    content: "A to Z",
                    type: "asc",
                  },
                  {
                    id: 3,
                    content: "Z to A",
                    type: "desc",
                  },
                ]}
              />
            </div>
          </div>
        )}

        {filteredResults && showResults && (
          <>
            <SearchResults emptyResults={emptyResults} />
          </>
        )}
        {searchError && (
          <p className="error">{searchError ? searchError : emptyResults}</p>
        )}
      </div>
      {isLoading && <Loading size="large" overlay={true} />}
    </section>
  );
};

export default SearchContainer;
